import { useCallback } from 'react';
import styled, { css } from 'styled-components';
import {
  AppAudioPlayerControlsSkip15SecBack,
  AppAudioPlayerControlsSkipBackFilled,
  AppRadioPlayerControlsBack
} from '@elfsight/icons';
import { getColorWithAlpha } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { PropsWithClassName } from '../../../../../types';
import { useTheme } from '../../../common';
import { useSource } from '../../../sources';
import { usePlayer } from '../../PlayerProvider';
import { DefaultButton } from './DefaultButton';
import { Icon } from './Icon';

interface PrevProps extends PropsWithClassName {}

export function Prev({ className, ...forwardingProps }: PrevProps) {
  const { textColor } = useTheme();
  const { sourceType } = useSettings();
  const {
    prev,
    progress: { playedSeconds = 0, played = 0 },
    seekTo,
    duration,
    loopPlaylist
  } = usePlayer();
  const { currentItemId, items } = useSource();

  const onClick = useCallback(() => {
    if (sourceType === 'rss') {
      seekTo((duration * played - 15) / duration);
    } else {
      prev();
    }
  }, [duration, played, prev, seekTo, sourceType]);

  const isTooShortPlayback = sourceType !== 'player' || playedSeconds < 3;
  const noPreviousTrack =
    sourceType !== 'rss' &&
    isTooShortPlayback &&
    currentItemId === 0 &&
    !loopPlaylist;

  const isRssNotStarted = sourceType === 'rss' && !playedSeconds;
  const disabled = !items?.length || noPreviousTrack || isRssNotStarted;

  const IconComponent = (() => {
    if (sourceType === 'rss') {
      return AppAudioPlayerControlsSkip15SecBack;
    }

    if (sourceType === 'radio') {
      return AppRadioPlayerControlsBack;
    }

    return AppAudioPlayerControlsSkipBackFilled;
  })();

  return (
    <Component
      {...forwardingProps}
      className={className}
      aria-label={!disabled ? 'Previous' : undefined}
      onClick={!disabled ? onClick : undefined}
      _disable={disabled}
    >
      <Icon as={IconComponent} fill={getColorWithAlpha(textColor, 1)} />
    </Component>
  );
}

const Component = styled(DefaultButton)<{
  _disable: boolean;
}>`
  margin-right: 0;
  margin-left: 0;
  opacity: ${({ _disable }) => (_disable ? 0.3 : 1)};
  ${({ _disable }) =>
    _disable &&
    css`
      pointer-events: none;
    `};
`;
